'use client';

import { useState, useEffect } from 'react';
import TodoItem from './TodoItem';
import { useAuth } from '@/lib/auth-context';

interface Todo {
  id: string;
  title: string;
  details: string | null;
  notes: string | null;
  due_date: string | null;
  delegate: string | null;
  sort_order: number;
  deleted_at: string | null;
  archived_at: string | null;
  decision_id: string | null;
  suggestion_reason: string | null;
  confidence: number | null;
  sensitive: boolean;
  critical: boolean;
  personal: boolean;
}

interface TodoArchiveProps {
  initialTodos: Todo[];
  onTodoChange?: () => void;
}

export default function TodoArchive({ initialTodos, onTodoChange }: TodoArchiveProps) {
  const { permissions } = useAuth();
  const [todos, setTodos] = useState<Todo[]>(initialTodos);

  useEffect(() => {
    setTodos(initialTodos);
  }, [initialTodos]);

  const handleRestore = async (id: string) => {
    // Optimistic update - remove from list
    setTodos(prev => prev.filter(t => t.id !== id));

    try {
      const response = await fetch(`/api/todos/${id}/restore`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to restore');
      onTodoChange?.();
    } catch (error) {
      console.error('Failed to restore todo:', error);
      setTodos(initialTodos);
    }
  };

  const handlePurge = async (id: string) => {
    const todo = todos.find(t => t.id === id);
    const title = todo?.title || 'this todo';

    if (!confirm(`Permanently delete "${title}"? This cannot be undone.`)) {
      return;
    }

    // Optimistic update - remove from list
    setTodos(prev => prev.filter(t => t.id !== id));

    try {
      const response = await fetch(`/api/todos/${id}/purge`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to purge');
      onTodoChange?.();
    } catch (error) {
      console.error('Failed to purge todo:', error);
      setTodos(initialTodos);
    }
  };

  const handlePurgeAll = async () => {
    if (!confirm('Permanently delete all archived todos? This cannot be undone.')) {
      return;
    }

    const previousTodos = [...todos];
    setTodos([]);

    try {
      // Purge all archived todos
      await Promise.all(
        previousTodos.map(todo =>
          fetch(`/api/todos/${todo.id}/purge`, { method: 'DELETE' })
        )
      );
      onTodoChange?.();
    } catch (error) {
      console.error('Failed to purge all todos:', error);
      setTodos(previousTodos);
    }
  };

  if (todos.length === 0) {
    return (
      <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">
        No archived todos.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {permissions.canPurge && (
        <div className="flex justify-end mb-2">
          <button
            onClick={handlePurgeAll}
            className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
          >
            Purge all archived
          </button>
        </div>
      )}
      {todos.map(todo => (
        <TodoItem
          key={todo.id}
          todo={todo}
          onDelete={() => {}}
          onRestore={permissions.canEditTodos ? handleRestore : undefined}
          onPurge={permissions.canPurge ? handlePurge : undefined}
          showDragHandle={false}
          showActions={permissions.canEditTodos || permissions.canPurge}
          isArchived={true}
        />
      ))}
    </div>
  );
}
